import React, { useEffect, useState } from 'react'
import { useFetchProductDetail } from 'features/ECommerce/api/queries/useFetchProductDetail'
import { Stack } from 'components/Stack'
import { MainDetails } from './components/MainDetails'
import { FurtherDetails } from './components/FurtherDetails'
import { AccurateData } from './components/AccurateData'
import { RelatedProductsHeader } from './components/RelatedProductsHeader'
import { RelatedProductsList } from './components/RelatedProductsList'
import {
  ProductDetailImage,
  Information,
  ProductDetailCard,
  StickyElement,
  ProductCTA,
} from './ProductDetail.styles'
import { Hr } from 'components/Hr'
import { CheckoutLayout } from 'features/components/CheckoutLayout'
import { Loader } from 'features/components/Loader'
import { useRouter } from 'hooks/useRouter'
import Image from 'next/image'
import { isElementVisible } from 'utils/dom'
import { Text } from 'components/Text'
import { VerticalSeparator } from 'components/VerticalSeparator'
import { Button } from 'components/Button'
import { EditQuantity } from 'components/EditQuantity'
import { useAddToCart } from 'features/ECommerce/hooks/useAddToCart'
import { formatCurrency } from 'utils/currency'
import { hasWeightLimit } from 'models/Product'
import { formatWeight } from 'utils/weight'
import { useTranslation } from 'hooks/useTranslation'
import { Desktop } from 'components/Responsive/Mobile'

export const ProductDetailLegacy = () => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const addToCart = useAddToCart()

  const { productId } = query as { productId: string }
  const { data: productDetail, status } = useFetchProductDetail(productId)

  const [isHidden, setIsHidden] = useState(true)
  const [quantity, setQuantity] = useState(1)

  useEffect(() => {
    const _scrollListener = () => {
      setIsHidden(false)

      const isCTAVisible = isElementVisible('.product-cta')
      const isFooterVisible = isElementVisible('.footer')

      if (isCTAVisible || isFooterVisible) {
        setIsHidden(true)
      }
    }
    document.addEventListener('scroll', _scrollListener)

    return () => document.removeEventListener('scroll', _scrollListener)
  })

  if (status === 'loading' || status === 'idle' || status === 'error') return <Loader />

  const isOutOfStock = productDetail.isHidden

  return (
    <CheckoutLayout hideCTA returnUrl="/products">
      <StickyElement isHidden={isHidden}>
        <ProductCTA>
          <Stack direction="column" gap="2">
            <Stack
              justifyContent="space-between"
              alignItems="center"
              gap={{ '@initial': '1', '@bp3': '7' }}
              direction={{ '@initial': 'column', '@bp3': 'row' }}
            >
              <Text size="h5" weight="medium">
                {productDetail.name}
              </Text>

              <Desktop breakpoint="bp3">
                <Stack gap="6" alignItems="center">
                  <VerticalSeparator size="9" />

                  <EditQuantity
                    size="small"
                    product={{ ...productDetail, quantity }}
                    onQuantityChange={(_, quantity) => setQuantity(quantity)}
                  />
                  <VerticalSeparator size="9" />
                </Stack>
              </Desktop>

              <Stack
                gap={{ '@initial': '1', '@bp3': '6' }}
                alignItems="center"
                direction={{ '@initial': 'column', '@bp3': 'row' }}
              >
                <Stack
                  direction={{ '@initial': 'row', '@bp3': 'column' }}
                  gap={{ '@initial': '1', '@bp3': '0' }}
                  alignItems="center"
                >
                  <Text color="primary-500">
                    <Text size="h5" weight="medium" color="primary-500">
                      {formatCurrency(productDetail.price.total)}
                      <Text>{t('common:per-unit')}</Text>
                    </Text>
                  </Text>
                  {hasWeightLimit(productDetail) && (
                    <Text>{`${formatWeight(productDetail.weight)}${t(
                      'e-commerce:per-unit'
                    )}`}</Text>
                  )}
                </Stack>
                <Button
                  onClick={() => addToCart(productDetail.productId, quantity, productDetail.name)}
                  disabled={isOutOfStock}
                >
                  {isOutOfStock ? t('e-commerce:detail.out-of-stock') : t('e-commerce:detail.cta')}
                </Button>
              </Stack>
            </Stack>
            {hasWeightLimit(productDetail) && (
              <Desktop breakpoint="bp3">
                <Text size="small" color="tertiary-700">
                  In order to make a purchase it must be equal to or greater than 30 lb, 60 lb, 90
                  lb or 120 lb. Treats will not affect the final weight of the order.
                </Text>
              </Desktop>
            )}
          </Stack>
        </ProductCTA>
      </StickyElement>

      <ProductDetailCard>
        <Stack direction="column" gap="4">
          <Stack direction={{ '@initial': 'column', '@bp2': 'row' }} gap="5">
            <ProductDetailImage>
              <Image
                src={productDetail.image}
                width={500}
                height={500}
                objectFit="contain"
                alt=""
              />
            </ProductDetailImage>
            <Information gap="5" direction="column" alignItems="flex-start">
              <MainDetails
                productDetail={productDetail}
                quantity={quantity}
                onQuantityChange={setQuantity}
              />
              <FurtherDetails productDetail={productDetail} />
              <AccurateData productDetail={productDetail} />
            </Information>
          </Stack>
          <Stack direction="column" gap={{ '@initial': '3', '@bp2': '4' }}>
            <Hr />
            <Stack direction="column" alignItems="center" gap={{ '@initial': '3', '@bp2': '5' }}>
              <RelatedProductsHeader />
              <RelatedProductsList slug={productDetail.slug} />
            </Stack>
          </Stack>
        </Stack>
      </ProductDetailCard>
    </CheckoutLayout>
  )
}

ProductDetailLegacy.displayName = 'ProductDetail'
